<template>
  <v-card flat class="panel-card">
    <div class="customer-body">
      <v-tooltip v-if="isCustomerChangeDisabled" bottom>
        <template #activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on" style="width: 100%">
            <v-autocomplete
              v-model="selectedUserId"
              :items="autocompleteItems"
              :loading="loadingUsers"
              item-text="userInfo"
              item-value="id"
              :label="$t('purchase.searchUser')"
              prepend-inner-icon="mdi-magnify"
              dense
              solo
              hide-details
              :search-input.sync="search"
              :disabled="isCustomerChangeDisabled"
              no-filter
              class="mb-4"
              :clearable="!isCustomerChangeDisabled">
              <template #item="{ item }">
                <v-list-item-content>
                  <v-list-item-title v-text="item.displayName" />
                  <v-list-item-subtitle v-if="item.cardId">
                    {{ $t('entities.users.attributes.cardId') }}:
                    {{ item.cardId }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </div>
        </template>
        <span>
          {{ $t('purchaseV2.customer.cannotChangeUserWithPack') }}
        </span>
      </v-tooltip>
      <v-autocomplete
        v-else
        v-model="selectedUserId"
        :items="autocompleteItems"
        :loading="loadingUsers"
        item-text="userInfo"
        item-value="id"
        :label="$t('purchase.searchUser')"
        prepend-inner-icon="mdi-magnify"
        dense
        solo
        hide-details
        :search-input.sync="search"
        no-filter
        class="mb-4"
        clearable>
        <template #item="{ item }">
          <v-list-item-content>
            <v-list-item-title v-text="item.displayName" />
            <v-list-item-subtitle v-if="item.cardId">
              {{ $t('entities.users.attributes.cardId') }}: {{ item.cardId }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-autocomplete>

      <div v-if="selectedCustomer" class="user-details">
        <div class="mb-4">
          <p class="username mb-3">{{ selectedCustomer.username }}</p>
          <p v-if="selectedCustomer.name" class="mb-3">
            {{ selectedCustomer.name }}
          </p>
          <div class="mb-4">
            <v-chip color="brown" dark class="mr-2 mb-2">
              <CoinIcon class="mr-2 pb-1" />
              {{ selectedCustomer.coins }}
            </v-chip>
            <v-chip color="primary" class="mr-2 mb-2">
              {{ selectedCustomer.experience.rank.name }}
            </v-chip>
            <v-btn
              color="warning"
              small
              depressed
              :disabled="!selectedCustomer.id"
              @click="openSimulateSessionDialog">
              <v-icon class="white--text" small>mdi-timer-sand</v-icon>
              {{ $t('entities.users.simulateSession') }}
            </v-btn>
          </div>
        </div>
        <div class="tables">
          <div class="table-block">
            <div class="table-title">
              {{ $t('purchaseV2.customer.topProducts') }}
            </div>
            <v-simple-table dense class="list-table">
              <tbody>
                <tr
                  v-for="item in selectedCustomer.topProducts.slice(0, 5)"
                  :key="item.name">
                  <td>
                    <div class="row-between">
                      <span>{{ item.name }}</span>
                      <span class="font-weight-bold">{{ item.quantity }}x</span>
                    </div>
                  </td>
                </tr>
                <tr v-if="!selectedCustomer.topProducts.length">
                  <td class="text-center grey--text text--darken-1">
                    {{ $t('purchase.noPurchases') }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <div class="table-block">
            <div class="table-title">
              {{ $t('purchaseV2.customer.topGames') }}
            </div>
            <v-simple-table dense class="list-table">
              <tbody>
                <tr
                  v-for="game in selectedCustomer.topGames.slice(0, 5)"
                  :key="game.name">
                  <td>
                    <div class="row-between">
                      <span>{{ game.name }}</span>
                      <span class="font-weight-bold">
                        {{ game.playtime }}h
                      </span>
                    </div>
                  </td>
                </tr>
                <tr v-if="!selectedCustomer.topGames.length">
                  <td class="text-center grey--text text--darken-1">
                    {{ $t('purchase.noGamesPlayed') }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </div>
        <v-chip
          v-if="selectedCustomer.offerStarterPack"
          color="error"
          small
          text-color="white"
          class="ml-2">
          {{ $t('purchase.offerStarterPack') }}
        </v-chip>
        <v-chip
          v-if="selectedCustomer.returningUser"
          color="error"
          small
          text-color="white"
          class="ml-2">
          {{ $t('purchase.returningUser') }}
        </v-chip>
      </div>
      <div v-else class="empty-state grey--text">
        {{ $t('purchase.userNotSelected') }}
      </div>
    </div>
    <SimulateSessionDialog
      v-model="showSimulateDialog"
      :entity="selectedCustomer || {}" />
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import CoinIcon from '@/components/CoinIcon.vue'
import User from '@/models/users/User'
import KpiUserApi from '@/services/kpi/KpiUserApi'
import GamesApi from '@/services/configuration/GamesApi'
import SimulateSessionDialog from '@/components/SimulateSessionDialog.vue'
import { CartItemType } from '@/store/modules/cart'

export default {
  name: 'PurchaseCustomer',
  components: {
    CoinIcon,
    SimulateSessionDialog,
  },
  data() {
    return {
      selectedUserId: null,
      users: [],
      loadingUsers: false,
      search: '',
      searchDebounce: null,
      showSimulateDialog: false,
    }
  },
  computed: {
    ...mapGetters('cart', [
      'selectedCustomer',
      'cartItems',
      'allCatalogVariants',
    ]),
    autocompleteItems() {
      const formatDisplayName = (user) => {
        const name = user.name || ''
        const username = user.username || ''
        return name ? `${name} (${username})` : username
      }
      const items = this.users.map((user) => ({
        userInfo: formatDisplayName(user),
        displayName: formatDisplayName(user),
        cardId: user.card_id != null ? String(user.card_id) : '',
        id: user.id,
      }))
      // Keep selected customer in the list so the autocomplete can display it
      // after search/users list changes (e.g. fetchUsers('') no longer includes them)
      if (this.selectedCustomer) {
        const id = this.selectedCustomer.id
        const alreadyInList = items.some((item) => `${item.id}` === `${id}`)
        if (!alreadyInList) {
          items.unshift({
            userInfo: formatDisplayName(this.selectedCustomer),
            displayName: formatDisplayName(this.selectedCustomer),
            cardId:
              this.selectedCustomer.card_id != null
                ? String(this.selectedCustomer.card_id)
                : '',
            id,
          })
        }
      }
      return items
    },
    hasPackInCart() {
      if (!this.cartItems?.length) return false

      const variantItems = this.cartItems.filter(
        (item) => item.type === CartItemType.VARIANT,
      )
      const hasPackVariant = variantItems.some((it) => it.variant.pack_type)
      if (hasPackVariant) return true

      const promotionItems = this.cartItems.filter(
        (item) => item.type === CartItemType.PROMOTION,
      )
      if (!promotionItems.length || !this.allCatalogVariants.length) {
        return false
      }

      return promotionItems.some((item) => {
        if (!item.promotion?.groups?.length) return false
        return item.promotion.groups.some((group) => {
          if (!group?.variants?.length) return false
          const groupVariantIds = new Set(
            group.variants.map((gv) => gv.arena_variant_id),
          )
          return this.allCatalogVariants.some(
            (variant) => groupVariantIds.has(variant.id) && variant.pack_type,
          )
        })
      })
    },
    isCustomerChangeDisabled() {
      return this.selectedCustomer && this.hasPackInCart
    },
  },
  methods: {
    async fetchUsers(query = '') {
      try {
        this.loadingUsers = true
        const params = this.getUserRequestParams(query)
        const response = await User.api.get(params)
        const data = response.data?.content || []
        this.users = data.map((user) => ({
          ...user,
          name: user.identity?.name || '',
          email: user.email?.address || '',
          coins: 0,
          experience: { rank: { name: '' } },
          topProducts: [],
          topGames: [],
          returningUser: user.returningUser || false,
          offerStarterPack: user.offerStarterPack || false,
        }))
      } catch (error) {
        console.warn('Searching for users failed.', error)
      } finally {
        this.loadingUsers = false
      }
    },
    getUserRequestParams(query = '') {
      if (!query) return 'size=10&page=1'
      const normalizedQuery = query.replace(/FFCARDID[^\d]+/i, '')
      if (!normalizedQuery) return 'size=10&page=1'
      const searchParams = this.getSearchParams(normalizedQuery)
      return `size=10&page=1&${searchParams}`
    },
    getSearchParams(searchTerm = '') {
      if (!searchTerm) return ''
      const searchAttributes = ['username', 'email', 'name', 'card_id', 'phone']
      const searchStrings = searchAttributes.map((it) => `${it}=${searchTerm}`)
      return searchStrings.join('&')
    },
    async loadUserDetails(userId) {
      if (!userId || !this.selectedCustomer) return
      await Promise.all([
        this.fetchWallet(userId),
        this.fetchExperience(userId),
        this.fetchTopPurchases(userId),
        this.fetchTopGames(userId),
      ])
    },
    async fetchWallet(userId) {
      try {
        const response = await User.api.getWallet(userId)
        const coins = response.data?.balance ?? 0
        const updatedCustomer = { ...this.selectedCustomer, coins }
        this.$store.dispatch('cart/setSelectedCustomer', updatedCustomer)
      } catch (error) {
        console.error('Fetching user wallet failed.', error)
        const updatedCustomer = { ...this.selectedCustomer, coins: 0 }
        this.$store.dispatch('cart/setSelectedCustomer', updatedCustomer)
      }
    },
    async fetchExperience(userId) {
      try {
        const response = await User.api.getXp(userId)
        const experience = response.data || { rank: { name: '' } }
        const updatedCustomer = { ...this.selectedCustomer, experience }
        this.$store.dispatch('cart/setSelectedCustomer', updatedCustomer)
      } catch (e) {
        console.warn('User XP API failed.', e)
        const updatedCustomer = {
          ...this.selectedCustomer,
          experience: { rank: { name: '' } },
        }
        this.$store.dispatch('cart/setSelectedCustomer', updatedCustomer)
      }
    },
    async fetchTopPurchases(userId) {
      try {
        const endDate = new Date()
        const startDate = new Date()
        startDate.setFullYear(endDate.getFullYear() - 1)
        const response = await KpiUserApi.getMostSoldToUser(
          userId,
          startDate,
          endDate,
        )
        const items = response.data || []
        const topProducts = items.map((item) => ({
          name: item.item_name || item.name,
          quantity: item.total_sold || item.quantity || 0,
        }))
        const updatedCustomer = { ...this.selectedCustomer, topProducts }
        this.$store.dispatch('cart/setSelectedCustomer', updatedCustomer)
      } catch (e) {
        console.warn('KPI user purchases API failed.', e)
        const updatedCustomer = { ...this.selectedCustomer, topProducts: [] }
        this.$store.dispatch('cart/setSelectedCustomer', updatedCustomer)
      }
    },
    async fetchTopGames(userId) {
      try {
        const endDate = new Date()
        const startDate = new Date()
        startDate.setFullYear(endDate.getFullYear() - 1)
        const response = await KpiUserApi.getMostPlayedByUser(
          userId,
          startDate,
          endDate,
        )
        const userGamesData = response.data || []
        if (!userGamesData.length) {
          const updatedCustomer = { ...this.selectedCustomer, topGames: [] }
          this.$store.dispatch('cart/setSelectedCustomer', updatedCustomer)
          return
        }
        const gameIds = userGamesData.map((game) => game.game_id)
        const searchParams = 'game_ids=' + gameIds.join('&game_ids=')
        const gamesResponse = await GamesApi.search(searchParams)
        const gamesMap = {}
        gamesResponse.data.data.forEach((game) => {
          gamesMap[game.id] = game.name
        })
        const topGames = userGamesData.map((game) => ({
          name: gamesMap[game.game_id] || game.name || '',
          playtime: Number(game.total_playtime_h || 0).toFixed(2),
        }))
        const updatedCustomer = { ...this.selectedCustomer, topGames }
        this.$store.dispatch('cart/setSelectedCustomer', updatedCustomer)
      } catch (e) {
        console.warn('KPI user games API failed.', e)
        const updatedCustomer = { ...this.selectedCustomer, topGames: [] }
        this.$store.dispatch('cart/setSelectedCustomer', updatedCustomer)
      }
    },
    openSimulateSessionDialog() {
      this.showSimulateDialog = true
    },
    resetCustomer() {
      this.selectedUserId = null
      this.$store.dispatch('cart/clearSelectedCustomer')
    },
    initializeFromStore() {
      const customer = { ...this.selectedCustomer }
      if (!customer?.id) return
      // Add user to the users list so autocomplete can display it
      this.users = [customer]
      // Set selectedUserId - the watcher will handle the rest
      this.selectedUserId = customer.id
    },
  },
  created() {
    this.initializeFromStore()
  },
  watch: {
    search(newVal) {
      const normalized = (newVal || '').replace(/FFCARDID[^\d]+/i, '')
      if (normalized !== newVal) {
        this.search = normalized
      }
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce)
      }
      this.searchDebounce = setTimeout(() => {
        this.fetchUsers(this.search)
      }, 400)
    },
    selectedUserId(newVal, oldVal) {
      // Prevent changing or clearing customer if there is a pack in cart
      if (this.hasPackInCart && this.selectedCustomer) {
        const currentCustomerId = this.selectedCustomer.id
        const isClearing = !newVal
        const isChanging = newVal && `${newVal}` !== `${currentCustomerId}`

        if (isClearing || isChanging) {
          // Revert the change - check if we're not already reverting to avoid loop
          if (`${oldVal}` !== `${currentCustomerId}`) {
            this.$nextTick(() => {
              this.selectedUserId = currentCustomerId
            })
          }
          this.$store.dispatch('showSnackbar', {
            color: 'error',
            message: this.$t('purchaseV2.customer.cannotChangeUserWithPack'),
            icon: 'error',
          })
          return
        }
      }

      this.$emit('customer-selected', newVal || null)
      if (!newVal) {
        this.$store.dispatch('cart/clearSelectedCustomer')
        return
      }
      const baseUser = this.users.find((u) => `${u.id}` === `${newVal}`) || {
        id: newVal,
        username: '',
        name: '',
        coins: 0,
        experience: { rank: { name: '' } },
        topProducts: [],
        topGames: [],
        returningUser: false,
        offerStarterPack: false,
      }
      const selectedCustomer = {
        ...baseUser,
        coins: baseUser.coins || 0,
        experience: baseUser.experience || { rank: { name: '' } },
        topProducts: [],
        topGames: [],
      }
      this.$store.dispatch('cart/setSelectedCustomer', selectedCustomer)
      this.loadUserDetails(newVal)
    },
  },
}
</script>

<style scoped>
.panel-card {
  border-radius: 8px;
  height: 100%;
  padding: 1rem;
  display: flex;
  overflow: hidden;
}

.customer-body {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  height: 100%;
}

.username {
  font-size: 1.5rem;
}

.name {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.tables {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  overflow-y: auto;
  padding-right: 0.25rem;
  flex: 1;
}

.table-block {
  width: 100%;
}

.table-block:not(:last-child) .list-table {
  width: auto;
  max-width: 100%;
}

.table-title {
  font-weight: 600;
  margin-bottom: 0.375rem;
}

.list-table {
  width: 100%;
}

.list-table td {
  border-bottom: none !important;
  padding: 0.5rem 0.25rem;
}

.row-between {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.empty-state {
  text-align: center;
  margin-top: 2rem;
}
</style>
